import * as PIXI from 'pixi.js';
import { AudioSystem } from '../assets/AudioSystem';

interface GameInfoPageOptions {
  onClose?: () => void;
  audioSystem?: AudioSystem | null;
}

export class GameInfoPage {
  private container: PIXI.Container;
  private visible: boolean = false;
  private scrollContainer: PIXI.Container;
  private scrollY: number = 0;
  private maxScroll: number = 0;
  private options: GameInfoPageOptions;

  constructor(options: GameInfoPageOptions = {}) {
    this.options = options;
    this.container = new PIXI.Container();
    this.scrollContainer = new PIXI.Container();
    this.setupPage();
    this.container.visible = false;
  }

  private setupPage(): void {
    const screenWidth = 1920;
    const screenHeight = 1080;

    // Backdrop
    const backdrop = new PIXI.Graphics();
    backdrop.beginFill(0x000000, 0.95);
    backdrop.drawRect(0, 0, screenWidth, screenHeight);
    backdrop.endFill();
    backdrop.eventMode = 'static';
    backdrop.cursor = 'pointer';
    // Right-click to close
    backdrop.on('rightdown', () => {
      this.options.audioSystem?.playSound('button_click');
      this.hide();
    });
    this.container.addChild(backdrop);

    // Panel
    const panelWidth = 1400;
    const panelHeight = 900;
    const panelX = (screenWidth - panelWidth) / 2;
    const panelY = (screenHeight - panelHeight) / 2;

    const panel = new PIXI.Graphics();
    panel.beginFill(0x1a1a1a, 0.95);
    panel.drawRoundedRect(0, 0, panelWidth, panelHeight, 12);
    panel.endFill();
    panel.lineStyle(3, 0x444444, 0.8);
    panel.drawRoundedRect(0, 0, panelWidth, panelHeight, 12);
    panel.x = panelX;
    panel.y = panelY;
    panel.eventMode = 'static';
    this.container.addChild(panel);

    // Title
    const title = new PIXI.Text('Game Information', {
      fontFamily: 'Courier New',
      fontSize: 48,
      fill: 0xffffff,
      fontWeight: 'bold',
    });
    title.x = panelX + 30;
    title.y = panelY + 30;
    this.container.addChild(title);

    // Scrollable area with mask
    const scrollMask = new PIXI.Graphics();
    scrollMask.beginFill(0x000000);
    scrollMask.drawRect(panelX + 30, panelY + 100, panelWidth - 60, panelHeight - 150);
    scrollMask.endFill();
    this.container.addChild(scrollMask);

    this.scrollContainer.x = panelX + 30;
    this.scrollContainer.y = panelY + 100;
    this.scrollContainer.mask = scrollMask;
    this.container.addChild(this.scrollContainer);

    let currentY = 0;
    const contentWidth = panelWidth - 60;

    const sections: Array<{ heading: string; body: string }> = [
      {
        heading: 'Gold & Economy',
        body:
          'Initial Gold: 150g (intentional scarcity for strategic decisions)\n\n' +
          'Gold Per Minute (GPM): Increases every 10 waves, caps at wave 150\n' +
          '• Waves 1-10: 5 GPM\n' +
          '• Waves 11-20: 7 GPM\n' +
          '• Waves 21-30: 9 GPM\n' +
          '• Waves 31-40: 11 GPM\n' +
          '• Waves 41-50: 13 GPM\n' +
          '• Waves 51-150: 15 GPM (maximum)\n' +
          '• Waves 151+: 15 GPM (frozen - no further increases)\n\n' +
          'Kill Gold: Increases every 15 waves (3g to 12g max)\n\n' +
          'Wave Completion Bonus: 5g (early), 10g (mid), 15g (late)\n\n' +
          'Boss Bonus: 75g\n\n' +
          'The economy is designed to create a hard limit - gold income stops scaling at wave 150, ensuring the game eventually becomes unwinnable.',
      },
      {
        heading: 'Enemy Scaling',
        body:
          'Enemy HP Scaling (Hybrid - Faster Early, Slower Late):\n' +
          '• Waves 1-50: 8% increase per wave\n' +
          '• Waves 51-150: 6% increase per wave\n' +
          '• Waves 151+: 3% increase per wave\n\n' +
          'Enemy Count Scaling:\n' +
          '• Matches HP scaling pattern (faster early, slower late)\n' +
          '• Starts at 15 enemies on wave 1\n' +
          '• Caps at 100 enemies by wave 150\n' +
          '• Remains at 100 enemies after wave 150\n\n' +
          'Enemy Damage: Flat amount throughout the game (no scaling)\n\n' +
          'The game is balanced against the player - enemies become progressively stronger while your economy has limits.',
      },
      {
        heading: 'Block Breaker Mechanic',
        body:
          'When enemies encounter a blocked path, they enter Block Breaker mode:\n\n' +
          '• Red Path: Enemy attacks the closest blocking building\n' +
          '• Attack Speed: +30% faster when breaking blocks\n' +
          '• Green Path: Enemy tries to reroute or phase through if completely stuck\n\n' +
          'Special Enemy Types:\n' +
          '• Ghost/Flying units: Ignore block breaker, phase through walls\n' +
          '• Regular enemies: Must break through or find alternate path\n\n' +
          'Strategic wall placement is crucial - enemies will break through if you block their path completely!',
      },
      {
        heading: 'Wave System',
        body:
          'Waves spawn enemies automatically when you press "Start Wave"\n\n' +
          'Auto Mode: Automatically starts the next wave after all enemies are defeated\n' +
          '• 5 second countdown (1x speed)\n' +
          '• 3 second countdown (2x speed)\n' +
          '• 1 second countdown (3x speed)\n\n' +
          'Boss Waves: Every 10 waves, a powerful boss enemy spawns\n\n' +
          'Mini-Boss Waves: Every 5 waves starting from wave 15\n\n' +
          'You cannot start a wave or enable Auto Mode without at least one tower placed.',
      },
      {
        heading: 'Building Restrictions',
        body:
          'During Active Waves:\n' +
          '• Wall building is blocked\n' +
          '• Tower building only allowed with Quick Build (must build on existing walls)\n\n' +
          'During Auto Mode:\n' +
          '• All building is blocked\n\n' +
          'Quick Build (Settings):\n' +
          '• Click tower button, then click on walls to auto-replace\n' +
          '• Works during waves for strategic tower placement\n\n' +
          'Quick Sell (Settings):\n' +
          '• Right-click buildings to instantly sell\n' +
          '• Warning: Can waste gold if not careful!',
      },
      {
        heading: 'Selling & Repairing',
        body:
          'Selling Buildings:\n' +
          '• Full HP: 50% refund of total cost (including upgrades)\n' +
          '• Damaged (<100% HP): Additional 60% penalty (40% of normal sell value)\n\n' +
          'Repairing Buildings:\n' +
          '• Cost: +5% of building cost per 10% missing HP (max 45%)\n' +
          '• Duration: ~1 second per 10% missing HP\n' +
          '• Towers are disabled during repair\n' +
          '• Cannot repair during waves or Auto Mode',
      },
      {
        heading: 'Tower MVP System',
        body:
          'The game tracks damage dealt by each tower during your run.\n\n' +
          '• The tower dealing the most damage gets a golden MVP indicator\n' +
          '• MVP indicator appears after wave 11\n' +
          '• Visual indicator: Golden crown with "MVP" text\n' +
          '• Indicator animates with a pulsing shine effect\n\n' +
          'Use this to identify your most effective tower placements!',
      },
    ];

    const headingStyle = new PIXI.TextStyle({
      fontFamily: 'Courier New',
      fontSize: 32,
      fill: 0xffe082,
      fontWeight: 'bold',
    });
    const bodyStyle = new PIXI.TextStyle({
      fontFamily: 'Courier New',
      fontSize: 24,
      fill: 0xffffff,
      wordWrap: true,
      wordWrapWidth: contentWidth - 30,
      lineHeight: 34,
    });

    for (const section of sections) {
      const heading = new PIXI.Text(section.heading, headingStyle);
      heading.x = 15;
      heading.y = currentY;
      this.scrollContainer.addChild(heading);
      currentY += heading.height + 12;

      const body = new PIXI.Text(section.body, bodyStyle);
      body.x = 15;
      body.y = currentY;
      this.scrollContainer.addChild(body);
      currentY += body.height + 35;
    }

    this.maxScroll = Math.max(0, currentY - (panelHeight - 150));

    // Scroll buttons
    const scrollUpBtn = new PIXI.Graphics();
    scrollUpBtn.beginFill(0x444444, 0.9);
    scrollUpBtn.drawRoundedRect(0, 0, 200, 40, 8);
    scrollUpBtn.endFill();
    scrollUpBtn.lineStyle(2, 0x888888);
    scrollUpBtn.drawRoundedRect(0, 0, 200, 40, 8);
    scrollUpBtn.x = panelX + (panelWidth - 200) / 2;
    scrollUpBtn.y = panelY + 50;
    scrollUpBtn.eventMode = 'static';
    scrollUpBtn.cursor = 'pointer';
    scrollUpBtn.on('pointerdown', () => {
      this.scrollY = Math.max(0, this.scrollY - 100);
      this.updateScroll();
    });
    this.container.addChild(scrollUpBtn);

    const scrollUpText = new PIXI.Text('▲ Scroll Up', {
      fontFamily: 'Courier New',
      fontSize: 18,
      fill: 0xffffff,
      align: 'center',
    });
    scrollUpText.x = scrollUpBtn.x + 100 - scrollUpText.width / 2;
    scrollUpText.y = scrollUpBtn.y + 20 - scrollUpText.height / 2;
    this.container.addChild(scrollUpText);

    const scrollDownBtn = new PIXI.Graphics();
    scrollDownBtn.beginFill(0x444444, 0.9);
    scrollDownBtn.drawRoundedRect(0, 0, 200, 40, 8);
    scrollDownBtn.endFill();
    scrollDownBtn.lineStyle(2, 0x888888);
    scrollDownBtn.drawRoundedRect(0, 0, 200, 40, 8);
    scrollDownBtn.x = panelX + (panelWidth - 200) / 2;
    scrollDownBtn.y = panelY + panelHeight - 50;
    scrollDownBtn.eventMode = 'static';
    scrollDownBtn.cursor = 'pointer';
    scrollDownBtn.on('pointerdown', () => {
      this.scrollY = Math.min(this.maxScroll, this.scrollY + 100);
      this.updateScroll();
    });
    this.container.addChild(scrollDownBtn);

    const scrollDownText = new PIXI.Text('▼ Scroll Down', {
      fontFamily: 'Courier New',
      fontSize: 18,
      fill: 0xffffff,
      align: 'center',
    });
    scrollDownText.x = scrollDownBtn.x + 100 - scrollDownText.width / 2;
    scrollDownText.y = scrollDownBtn.y + 20 - scrollDownText.height / 2;
    this.container.addChild(scrollDownText);

    // Close button
    const closeButton = new PIXI.Graphics();
    closeButton.beginFill(0x006600, 0.9);
    closeButton.drawRoundedRect(0, 0, 250, 50, 8);
    closeButton.endFill();
    closeButton.lineStyle(2, 0x00aa00);
    closeButton.drawRoundedRect(0, 0, 250, 50, 8);
    closeButton.x = panelX + panelWidth - 280;
    closeButton.y = panelY + panelHeight - 70;
    closeButton.eventMode = 'static';
    closeButton.cursor = 'pointer';
    closeButton.on('pointerdown', () => {
      this.options.audioSystem?.playSound('button_click');
      this.hide();
    });
    this.container.addChild(closeButton);

    const closeText = new PIXI.Text('Close', {
      fontFamily: 'Courier New',
      fontSize: 24,
      fill: 0xffffff,
      fontWeight: 'bold',
    });
    closeText.x = closeButton.x + closeButton.width / 2 - closeText.width / 2;
    closeText.y = closeButton.y + closeButton.height / 2 - closeText.height / 2;
    this.container.addChild(closeText);
  }

  private updateScroll(): void {
    const panelY = (1080 - 900) / 2;
    this.scrollContainer.y = panelY + 100 - this.scrollY;
  }

  show(): void {
    this.visible = true;
    this.container.visible = true;
    this.scrollY = 0;
    this.updateScroll();
    // Enable pointer events on the info pages canvas
    if ((window as any).infoPagesCanvas) {
      (window as any).infoPagesCanvas.style.pointerEvents = 'auto';
    }
  }

  hide(): void {
    this.visible = false;
    this.container.visible = false;
    // Disable pointer events when hidden
    if ((window as any).infoPagesCanvas) {
      (window as any).infoPagesCanvas.style.pointerEvents = 'none';
    }
    this.options.onClose?.();
  }

  getContainer(): PIXI.Container {
    return this.container;
  }
}

